import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatCurrencyFromCents } from '../../utils/api'
import DealDetailModal from '../deals/DealDetailModal'
import QuoteNotesLayout from './QuoteNotes'
import DealLink from '../shared/DealLink.jsx'
import ReturnRequestDialog from './ReturnRequestDialog.jsx'
import QueueHeader from '../shared/QueueHeader.jsx'
import DecisionDue from './DecisionDue.jsx'
import { CustomerMessagesView } from '../deals/quote-assessment/CustomerMessagesPanel.jsx'
import { useCustomerMessages, itemDecisions, decisionBadgeText } from '../deals/quote-assessment/customerMessages.js'

// Design System (matching original warehouse-system)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Components
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick }) => {
  const variants = {
    primary: { background: theme.colors.greenOnions, color: theme.colors.white },
    secondary: { background: theme.colors.charcoal, color: theme.colors.white },
    warning: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${theme.colors.grey200}`,
    borderTopColor: theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

// Progress Bar Component
const ProgressBar = ({ current, total, label }) => {
  const percent = total > 0 ? Math.round((current / total) * 100) : 0
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '14px', color: theme.colors.grey500 }}>{current} of {total}</span>
      </div>
      <div style={{
        height: '8px',
        background: theme.colors.grey200,
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${percent}%`,
          background: theme.colors.greenOnions,
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  )
}

function IssueTrades() {
  const [deals, setDeals] = useState([])
  // Quotes past day 14 with no reply, from Prism's decision_due queue.
  const [decisionDeals, setDecisionDeals] = useState([])
  const [tab, setTab] = useState('active') // 'active' (awaiting response) | 'decision' (day 14, no reply)
  const [loading, setLoading] = useState(true)
  const [selectedDeal, setSelectedDeal] = useState(null)
  // The seller's answers on the open deal (My Quotes messages): the conversation
  // and, per item, the decision staff finish here.
  const conversation = useCustomerMessages(selectedDeal?.id, selectedDeal?.quoteId)
  const decisions = itemDecisions(conversation.messages)
  const [modalDeal, setModalDeal] = useState(null)
  const [returningItem, setReturningItem] = useState(null) // item the customer wants back
  const [itemSelections, setItemSelections] = useState({})
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  // Already-queued items are collapsed by default; this reveals them.
  const [showQueued, setShowQueued] = useState(false)

  // Photography queue progress state
  const [draftProgress, setDraftProgress] = useState({
    status: 'idle', // 'idle' | 'queueing' | 'done'
    current: 0,
    total: 0,
    results: []  // Array of { item, success, error }
  })

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    setError(null)
    try {
      const [response, due] = await Promise.all([
        api.get('/api/warehouse/issue-trades'),
        api.get('/api/warehouse/decisions/due')
      ])
      setDeals(response.deals || response.items || [])
      setDecisionDeals(due.deals || [])
    } catch (err) {
      setError(err.message || 'Failed to load issue trades')
    } finally {
      setLoading(false)
    }
  }

  function selectDeal(deal) {
    setSelectedDeal(deal)
    setShowQueued(false) // collapse already-queued items on each open
    // Select every item that still needs action by default. Items already in the
    // photography queue (a passed item is auto-queued at testing save) are left
    // unselected so they are not re-queued — see `alreadyQueued`.
    const selections = {}
    ;(deal.items || []).forEach(item => {
      selections[item.id] = !item.alreadyQueued
    })
    setItemSelections(selections)
  }

  function toggleItemSelection(itemId) {
    // Already-queued items are read-only — they cannot be toggled back in.
    const item = (selectedDeal?.items || []).find(i => i.id === itemId)
    if (item?.alreadyQueued) return
    setItemSelections(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }

  function keepAll() {
    const selections = {}
    ;(selectedDeal?.items || []).forEach(item => {
      selections[item.id] = !item.alreadyQueued
    })
    setItemSelections(selections)
  }

  async function handleAccept() {
    if (!selectedDeal) return
    const keptItems = (selectedDeal.items || []).filter(item => itemSelections[item.id])
    // An already-queued item is unselected but NOT rejected — it is simply done.
    // Only genuinely deselected items become Closed Lost rejections.
    const removedItems = (selectedDeal.items || []).filter(item => !itemSelections[item.id] && !item.alreadyQueued)
    // A deal with items already through photography (and paid) must never be
    // rejected wholesale. reject-all forces the WHOLE deal to Closed Lost, which
    // would abandon those processed items — so it is only ever valid when nothing
    // on the deal is already queued.
    const hasQueuedItems = (selectedDeal.items || []).some(item => item.alreadyQueued)

    if (keptItems.length === 0) {
      if (hasQueuedItems) {
        // Nothing left to queue. Deselecting the rest rejects only those rest;
        // the queued/paid items and the deal itself stay put (finalize settles
        // the deal from item state, it does not reject-all).
        if (removedItems.length === 0) {
          alert('Nothing to do — the remaining items are already queued for photography.')
          return
        }
        if (!confirm(
          `Reject ${removedItems.length} remaining item${removedItems.length !== 1 ? 's' : ''} → Closed Lost?\n\n` +
          `Items already queued for photography are unaffected and the deal stays open.\n\nContinue?`
        )) return
        // Fall through: nothing is queued, finalize rejects removedItems only.
      } else {
        if (!confirm('No items selected. This will reject the entire trade and move it to Closed Lost. Continue?')) return
        // If no items kept and none already queued, use reject-all
        return handleReject()
      }
    } else {
      // Build item-by-item listing details
      const itemDetails = keptItems.map(item => {
        const condition = item.isBroken ? 'Spares or Repair' : (item.overallCondition || 'Good')
        const shortName = item.name?.length > 40 ? item.name.substring(0, 40) + '...' : item.name
        return `• ${shortName} → ${condition}`
      }).join('\n')

      // Confirmation for processing
      const msg = `This will queue ${keptItems.length} item${keptItems.length !== 1 ? 's' : ''} for photography:\n\n` +
        itemDetails +
        `\n\nDeal moves to Awaiting Payment.` +
        (removedItems.length > 0 ? `\n${removedItems.length} rejected item${removedItems.length !== 1 ? 's' : ''} → Closed Lost` : '') +
        `\n\nContinue?`

      if (!confirm(msg)) return
    }

    setProcessing(true)
    setError(null)

    // Initialize progress state
    setDraftProgress({
      status: 'queueing',
      current: 0,
      total: keptItems.length,
      results: keptItems.map(item => ({ item, status: 'pending' }))
    })

    try {
      // Queue items one at a time for progress tracking
      const results = []
      for (let i = 0; i < keptItems.length; i++) {
        const item = keptItems[i]

        // Update progress to show current item
        setDraftProgress(prev => ({
          ...prev,
          current: i,
          results: prev.results.map((r, idx) =>
            idx === i ? { ...r, status: 'queueing' } : r
          )
        }))

        try {
          // Queue individual item for photography
          await api.post('/api/warehouse/issue-trades/queue-photography', {
            dealId: selectedDeal.id,
            item: item
          })

          results.push({ item, success: true })

          // Update progress with success
          setDraftProgress(prev => ({
            ...prev,
            current: i + 1,
            results: prev.results.map((r, idx) =>
              idx === i ? { ...r, status: 'success' } : r
            )
          }))
        } catch (err) {
          results.push({ item, success: false, error: err.message })

          // Update progress with failure
          setDraftProgress(prev => ({
            ...prev,
            current: i + 1,
            results: prev.results.map((r, idx) =>
              idx === i ? { ...r, status: 'failed', error: err.message } : r
            )
          }))
        }
      }

      // Finalize the deal (move stages, handle rejected items)
      await api.post('/api/warehouse/issue-trades/finalize', {
        dealId: selectedDeal.id,
        acceptedCount: results.filter(r => r.success).length,
        rejectedItems: removedItems
      })

      // Update progress to done
      setDraftProgress(prev => ({ ...prev, status: 'done' }))

      // Show summary after brief delay
      setTimeout(() => {
        const successCount = results.filter(r => r.success).length
        const failCount = results.filter(r => !r.success).length

        if (failCount === 0) {
          // All succeeded - auto close after showing results
          setTimeout(() => {
            setSelectedDeal(null)
            setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
            loadDeals()
          }, 2000)
        }
      }, 500)

    } catch (err) {
      setError(err.message || 'Failed to process trade')
      setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
    } finally {
      setProcessing(false)
    }
  }

  async function handleCreateRepair() {
    if (!selectedDeal) return
    const keptItems = (selectedDeal.items || []).filter(item => itemSelections[item.id])
    const brokenItems = keptItems.filter(item => item.isBroken)

    if (brokenItems.length === 0) return

    const itemDetails = brokenItems.map(item => {
      const shortName = item.name?.length > 40 ? item.name.substring(0, 40) + '...' : item.name
      return `• ${shortName} → ${item.brokenDetails || 'Broken'}`
    }).join('\n')

    const removedItems = (selectedDeal.items || []).filter(item => !itemSelections[item.id] && !item.alreadyQueued)

    const msg = `Send ${brokenItems.length} item${brokenItems.length !== 1 ? 's' : ''} to repair:\n\n` +
      itemDetails +
      `\n\nItems will enter the repair queue as "Not Started".\nNo items will be queued for photography.\nDeal moves to Awaiting Payment.` +
      (removedItems.length > 0 ? `\n${removedItems.length} rejected → Closed Lost` : '') +
      `\n\nContinue?`

    if (!confirm(msg)) return

    setProcessing(true)
    setError(null)

    // Initialize progress state
    setDraftProgress({
      status: 'creating',
      current: 0,
      total: brokenItems.length,
      results: brokenItems.map(item => ({ item, status: 'pending' }))
    })

    try {
      await api.post('/api/warehouse/issue-trades/create-repair', {
        dealId: selectedDeal.id,
        items: brokenItems
      })

      // Finalize the deal (move to Awaiting Payment, handle rejected items)
      await api.post('/api/warehouse/issue-trades/finalize', {
        dealId: selectedDeal.id,
        acceptedCount: brokenItems.length,
        rejectedItems: removedItems
      })

      // Mark all as success
      setDraftProgress({
        status: 'done',
        current: brokenItems.length,
        total: brokenItems.length,
        results: brokenItems.map(item => ({ item, status: 'success' })),
        isRepair: true
      })

      // Auto close after showing results
      setTimeout(() => {
        setSelectedDeal(null)
        setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
        loadDeals()
      }, 2500)

    } catch (err) {
      setError(err.message || 'Failed to create repair items')
      setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
    } finally {
      setProcessing(false)
    }
  }

  // A wrong test result (e.g. a missing part that later turned up): put the item
  // back on the Testing list. The re-test there replaces the first result.
  async function handleSendBackToTesting(item) {
    if (!selectedDeal) return
    if (!confirm(`Send "${item.name}" back to testing?\n\nIt leaves Issue Trades and shows on the Testing page again.`)) return

    setProcessing(true)
    setError(null)
    try {
      const response = await api.post('/api/warehouse/issue-trades/send-back-to-testing', {
        dealId: selectedDeal.id,
        quoteItemId: item.quoteItemId || item.id
      })
      // The item has moved; these are follow-ups the operator should know about.
      if (response?.warnings?.length) alert(response.warnings.join('\n'))
      setSelectedDeal(null)
      await loadDeals()
    } catch (err) {
      setError(err.message || 'Failed to send the item back to testing')
    } finally {
      setProcessing(false)
    }
  }

  // An item was listed or binned. A failed note, or a failed photography
  // queue, comes back as a warning: say so, then reload.
  async function handleDecided(warnings) {
    if (warnings?.length) alert(warnings.join('\n'))
    await loadDeals()
  }

  // The seller replied after day 14: process the deal as usual.
  function openDecisionDeal(quoteId) {
    const deal = deals.find(d => d.quoteId === quoteId)
    if (deal) selectDeal(deal)
  }

  // The item moved to "return requested". A failed quote note comes back as a
  // warning: say so, then leave the deal, as the item no longer belongs here.
  async function handleReturnRequested(response) {
    setReturningItem(null)
    if (response?.warnings?.length) alert(response.warnings.join('\n'))
    setSelectedDeal(null)
    await loadDeals()
  }

  async function handleReject() {
    if (!selectedDeal) return
    if (!confirm('Are you sure you want to reject this entire trade?\n\nNo items will be queued for photography.\nThe deal will be moved to Closed Lost.')) return

    setProcessing(true)
    setError(null)
    try {
      await api.post('/api/warehouse/issue-trades/reject-all', {
        dealId: selectedDeal.id,
        reason: 'Customer rejected revised offer'
      })
      alert('Deal moved to Closed Lost')
      setSelectedDeal(null)
      await loadDeals()
    } catch (err) {
      setError(err.message || 'Failed to reject trade')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spinner size={40} />
      </div>
    )
  }

  // Deal Detail View
  if (selectedDeal) {
    const keptItems = (selectedDeal.items || []).filter(item => itemSelections[item.id])
    // Already-queued items are done, not rejected — keep them out of the Closed Lost set.
    const removedItems = (selectedDeal.items || []).filter(item => !itemSelections[item.id] && !item.alreadyQueued)
    // Items still needing action (shown first), and the already-queued ones
    // (collapsed by default). hasQueuedItems gates the wholesale "Move All to
    // Closed Lost" action, which must never abandon processed items.
    const activeItems = (selectedDeal.items || []).filter(item => !item.alreadyQueued)
    const queuedItems = (selectedDeal.items || []).filter(item => item.alreadyQueued)
    const hasQueuedItems = queuedItems.length > 0
    const keptTotal = keptItems.reduce((sum, item) => sum + (item.isBroken ? (item.sparesAmount || 0) : (item.buyPrice || 0)), 0)

    return (
      <QuoteNotesLayout dealId={selectedDeal.id} mainWidth={600} style={{ padding: '24px' }}>
        {/* Back Button */}
        <Button variant="ghost" size="sm" onClick={() => setSelectedDeal(null)} style={{ marginBottom: '24px' }}>
          ← Back
        </Button>

        {/* Deal Header */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: theme.colors.grey400, marginBottom: '4px' }}>Deal ID</div>
            {/* The human reference (TI-XXXXX, or the legacy numeric deal id on
                mirrored quotes) — the id a customer or operator can match to.
                The quote UUID is round-tripped separately, not shown. */}
            <div style={{ fontFamily: theme.fonts.sans, fontSize: '16px', fontWeight: 600 }}><DealLink dealId={selectedDeal.id} reference={selectedDeal.reference} newTab /></div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: theme.colors.grey400, marginBottom: '4px' }}>Customer</div>
            {/* Name when we have one, otherwise the email. Never the reference /
                deal id — that lives in the Deal ID field above. */}
            <div style={{ fontSize: '15px', fontWeight: 500 }}>
              {selectedDeal.customerName || selectedDeal.customerEmail || '—'}
            </div>
          </div>
        </Card>

        {/* What the seller has written and decided on My Quotes */}
        <div style={{ marginBottom: '24px' }}>
          <CustomerMessagesView
            conversation={conversation}
            items={(selectedDeal.items || []).map(i => ({ quote_item_id: i.id, name: i.name || i.productName }))}
          />
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <Button variant="ghost" size="sm" onClick={keepAll}>Keep All</Button>
          <div style={{ flex: 1 }} />
          <Button variant="danger" size="sm" onClick={handleReject} disabled={processing}>
            Reject Entire Deal
          </Button>
        </div>

        {/* Items List */}
        <Card style={{ marginBottom: '24px' }} padding={false}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.colors.grey200}`, fontWeight: 600 }}>
            Items ({activeItems.length})
          </div>

          <div style={{ padding: '12px' }}>
            {/* Items still needing action — always shown, on top. */}
            {activeItems.map((item, index) => {
              const isKept = itemSelections[item.id]
              const effectivePrice = item.isBroken ? item.sparesAmount : item.buyPrice

              return (
                <div
                  key={item.id || index}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px', marginBottom: '8px',
                    background: isKept ? theme.colors.greenLight : theme.colors.grey100,
                    borderRadius: theme.radius.md,
                    opacity: isKept ? 1 : 0.5
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px', fontWeight: 500,
                      textDecoration: isKept ? 'none' : 'line-through',
                      marginBottom: '4px'
                    }}>
                      {item.name || item.productName}
                    </div>
                    {decisions.has(item.id) && (
                      <div style={{ marginBottom: '4px' }} data-testid="seller-decision">
                        <Badge color={decisions.get(item.id).decision === 'accept' ? 'green' : 'amber'}>
                          {decisionBadgeText(decisions.get(item.id), formatCurrencyFromCents)}
                        </Badge>
                      </div>
                    )}
                    {item.isBroken && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Badge color="red">Broken</Badge>
                        {item.brokenDetails && (
                          <span style={{ fontSize: '12px', color: theme.colors.grey400 }}>{item.brokenDetails}</span>
                        )}
                        <Button variant="warning" size="sm" onClick={() => handleSendBackToTesting(item)} disabled={processing}>
                          Send back to testing
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setReturningItem(item)} disabled={processing}>
                          Customer wants it back
                        </Button>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ textAlign: 'right', minWidth: '70px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        {formatCurrency(effectivePrice || 0)}
                      </div>
                      {item.isBroken && item.buyPrice !== effectivePrice && (
                        <div style={{ fontSize: '11px', color: theme.colors.grey400, textDecoration: 'line-through' }}>
                          {formatCurrency(item.buyPrice || 0)}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => toggleItemSelection(item.id)}
                      style={{
                        width: '32px', height: '32px', borderRadius: '50%', border: 'none',
                        background: isKept ? theme.colors.grey100 : theme.colors.greenLight,
                        color: isKept ? theme.colors.grey400 : theme.colors.greenOnions,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '16px', fontWeight: 600
                      }}
                      title={isKept ? 'Remove item' : 'Keep item'}
                    >
                      {isKept ? '×' : '✓'}
                    </button>
                  </div>
                </div>
              )
            })}

            {/* Already queued for photography — collapsed by default, shown small
                and greyed as read-only context, never re-queued. */}
            {queuedItems.length > 0 && (
              <>
                <button
                  onClick={() => setShowQueued(v => !v)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: theme.colors.grey500, fontSize: '12px', fontWeight: 600,
                    padding: '8px 6px', marginTop: activeItems.length > 0 ? '4px' : 0,
                    display: 'flex', alignItems: 'center', gap: '6px', width: '100%'
                  }}
                >
                  <span style={{ fontSize: '10px' }}>{showQueued ? '▾' : '▸'}</span>
                  {showQueued
                    ? `Hide ${queuedItems.length} already queued for photography`
                    : `Show ${queuedItems.length} already queued for photography`}
                </button>
                {showQueued && queuedItems.map((item, index) => (
                  <div
                    key={item.id || `queued-${index}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 10px', marginBottom: '4px',
                      background: theme.colors.grey100, borderRadius: theme.radius.sm
                    }}
                  >
                    <span style={{
                      fontSize: '12px', color: theme.colors.grey500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px'
                    }}>
                      {item.name || item.productName}
                    </span>
                    <span style={{ fontSize: '11px', color: theme.colors.grey400, flexShrink: 0 }}>📷 Queued</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </Card>

        {/* Removed Items Summary */}
        {removedItems.length > 0 && (
          <Card style={{ marginBottom: '24px', background: theme.colors.grey100 }}>
            <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
              <strong>{removedItems.length} item{removedItems.length > 1 ? 's' : ''}</strong> will be moved to a new Closed Lost deal
            </div>
          </Card>
        )}

        {/* Summary */}
        <Card style={{ marginBottom: '24px', background: theme.colors.charcoal, color: theme.colors.white }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>Payment Total</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatCurrency(keptTotal)}</div>
            </div>
            <div style={{ fontSize: '13px', opacity: 0.7 }}>
              {keptItems.length} item{keptItems.length !== 1 ? 's' : ''}
            </div>
          </div>
        </Card>

        {error && (
          <div style={{
            background: theme.colors.redLight, color: theme.colors.crimson,
            padding: '12px 16px', borderRadius: theme.radius.md,
            fontSize: '14px', marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {/* Progress Bar during processing */}
        {(draftProgress.status === 'queueing' || draftProgress.status === 'creating') && (
          <Card style={{ marginBottom: '16px' }}>
            <ProgressBar
              current={draftProgress.current}
              total={draftProgress.total}
              label={draftProgress.isRepair ? "Sending to Repair" : "Queueing for Photography"}
            />
            <div style={{ marginTop: '12px' }}>
              {draftProgress.results.map((result, idx) => {
                const condition = result.item.isBroken ? 'Spares or Repair' : (result.item.overallCondition || 'Good')
                return (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 12px', marginBottom: '8px',
                    background: result.status === 'success' ? theme.colors.greenLight :
                               result.status === 'failed' ? theme.colors.redLight :
                               result.status === 'queueing' ? theme.colors.amberLight :
                               theme.colors.grey100,
                    borderRadius: theme.radius.md
                  }}>
                    <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>
                      {result.status === 'success' ? '✅' :
                       result.status === 'failed' ? '❌' :
                       result.status === 'queueing' ? <Spinner size={16} /> :
                       '⏳'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {result.item.name}
                      </div>
                      <div style={{ fontSize: '12px', color: theme.colors.grey500 }}>
                        {condition}
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: theme.colors.grey400, flexShrink: 0 }}>
                      {result.status === 'pending' ? 'Waiting...' :
                       result.status === 'queueing' ? 'Queueing...' :
                       result.status === 'success' ? 'Queued' :
                       'Failed'}
                    </span>
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        {/* Completion Summary */}
        {draftProgress.status === 'done' && (
          <Card style={{ marginBottom: '16px', background: draftProgress.isRepair ? theme.colors.purpleLight : theme.colors.greenLight }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{draftProgress.isRepair ? '🔧' : '✅'}</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: draftProgress.isRepair ? theme.colors.purple : theme.colors.greenOnions, marginBottom: '8px' }}>
                {draftProgress.isRepair ? 'Sent to Repair' : 'Queued for Photography'}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.grey500 }}>
                {draftProgress.isRepair
                  ? `${draftProgress.results.filter(r => r.status === 'success').length} item${draftProgress.results.filter(r => r.status === 'success').length !== 1 ? 's' : ''} added to repair queue`
                  : (<>{draftProgress.results.filter(r => r.status === 'success').length} of {draftProgress.total} queued for photography
                    {draftProgress.results.some(r => r.status === 'failed') && (
                      <span style={{ color: theme.colors.crimson }}>
                        {' '}• {draftProgress.results.filter(r => r.status === 'failed').length} failed
                      </span>
                    )}</>)
                }
              </div>
              <div style={{ marginTop: '16px' }}>
                <Button variant="ghost" size="sm" onClick={() => {
                  setSelectedDeal(null)
                  setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
                  loadDeals()
                }}>
                  ← Back to Issue Trades
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Info message about what will happen */}
        {keptItems.length > 0 && draftProgress.status === 'idle' && (
          <div style={{
            background: theme.colors.blueLight, color: theme.colors.imBlue,
            padding: '12px 16px', borderRadius: theme.radius.md,
            fontSize: '13px', marginBottom: '16px'
          }}>
            <strong>Items to queue for photography:</strong>
            <div style={{ marginTop: '8px' }}>
              {keptItems.map((item, idx) => {
                const condition = item.isBroken ? 'Spares or Repair' : (item.overallCondition || 'Good')
                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: idx < keptItems.length - 1 ? '1px solid rgba(78,128,152,0.2)' : 'none' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                      {item.name}
                    </span>
                    <span style={{ fontWeight: 600, flexShrink: 0 }}>
                      {condition}
                    </span>
                  </div>
                )
              })}
            </div>
            <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid rgba(78,128,152,0.3)' }}>
              Deal moves to Awaiting Payment
              {removedItems.length > 0 && <span> • {removedItems.length} rejected → Closed Lost</span>}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {draftProgress.status === 'idle' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Button
              onClick={handleAccept}
              disabled={processing || (keptItems.length === 0 && hasQueuedItems && removedItems.length === 0)}
              fullWidth
              size="lg"
            >
              {keptItems.length > 0 ? (
                `Queue ${keptItems.length} for Photography`
              ) : hasQueuedItems ? (
                // The queued/paid items stay; only the deselected rest is rejected.
                removedItems.length > 0
                  ? `Reject ${removedItems.length} Remaining Item${removedItems.length !== 1 ? 's' : ''}`
                  : 'Nothing to Queue'
              ) : (
                'Move All to Closed Lost'
              )}
            </Button>

            {keptItems.some(item => item.isBroken) && keptItems.length > 0 && (
              <Button
                variant="warning"
                onClick={handleCreateRepair}
                disabled={processing}
                fullWidth
                size="lg"
              >
                {`Create Repair (${keptItems.filter(i => i.isBroken).length} broken item${keptItems.filter(i => i.isBroken).length !== 1 ? 's' : ''})`}
              </Button>
            )}
          </div>
        )}

        {keptItems.length === 0 && draftProgress.status === 'idle' && (
          <p style={{ textAlign: 'center', fontSize: '13px', color: theme.colors.grey400, marginTop: '12px' }}>
            {hasQueuedItems
              ? (removedItems.length > 0
                  ? 'Remaining items will be rejected → Closed Lost. Already-queued items are unaffected.'
                  : 'All remaining items are already queued for photography.')
              : 'All items removed - deal will be moved to Closed Lost'}
          </p>
        )}
        {returningItem && (
          <ReturnRequestDialog
            dealId={selectedDeal.id}
            item={{ quoteItemId: returningItem.quoteItemId || returningItem.id, name: returningItem.name }}
            onDone={handleReturnRequested}
            onClose={() => setReturningItem(null)}
          />
        )}
      </QuoteNotesLayout>
    )
  }

  // List View
  // A quote past day 14 is shown under Decision due only.
  const decisionQuoteIds = new Set(decisionDeals.map(d => d.quoteId))
  const activeDeals = deals.filter(deal => !decisionQuoteIds.has(deal.quoteId))
  const issueQuoteIds = new Set(deals.map(d => d.quoteId))

  const tabs = [
    { key: 'active', label: 'Active', sub: 'awaiting response', count: activeDeals.length },
    { key: 'decision', label: 'Decision due', sub: 'day 14, no reply', count: decisionDeals.length }
  ]

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <QueueHeader title="Issue Trades" description="Deals with broken or rejected items awaiting resolution">
        <Button variant="ghost" onClick={loadDeals}>↻ Refresh</Button>
      </QueueHeader>

      {error && (
        <Card style={{ background: theme.colors.redLight, marginBottom: '20px' }}>
          <p style={{ color: theme.colors.crimson, margin: 0 }}>{error}</p>
        </Card>
      )}

      {/* Active / Decision due tabs */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', borderBottom: `1px solid ${theme.colors.grey200}` }}>
        {tabs.map(t => {
          const selected = tab === t.key
          const urgent = t.key === 'decision' && t.count > 0
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '10px 2px', marginBottom: '-1px',
                borderBottom: `2px solid ${selected ? theme.colors.charcoal : 'transparent'}`,
                color: selected ? theme.colors.charcoal : theme.colors.grey500,
                fontWeight: 600, fontSize: '14px',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <span>{t.label} <span style={{ fontWeight: 400, color: theme.colors.grey400 }}>· {t.sub}</span></span>
              <span style={{
                background: urgent ? theme.colors.redLight : theme.colors.grey200,
                color: urgent ? theme.colors.crimson : theme.colors.grey500,
                borderRadius: '10px', padding: '1px 9px', fontSize: '12px', fontWeight: 600
              }}>{t.count}</span>
            </button>
          )
        })}
      </div>

      {tab === 'decision' ? (
        decisionDeals.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Nothing to decide</h3>
            <p style={{ color: theme.colors.grey500 }}>No trade has reached day 14 without a reply</p>
          </Card>
        ) : (
          <DecisionDue
            deals={decisionDeals}
            onOpenDeal={(quoteId) => issueQuoteIds.has(quoteId) ? openDecisionDeal(quoteId) : undefined}
            onMoved={handleDecided}
          />
        )
      ) : activeDeals.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>{deals.length === 0 ? '✅' : '📨'}</div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
            {deals.length === 0 ? 'No Issue Trades' : 'No active trades'}
          </h3>
          <p style={{ color: theme.colors.grey500 }}>
            {deals.length === 0 ? 'All deals are resolved' : 'Every trade is waiting for a decision'}
          </p>
        </Card>
      ) : (
        <div>
          {activeDeals.map(deal => {
            const brokenCount = (deal.items || []).filter(i => i.isBroken).length
            const dealId = deal.id || deal.deal_id
            const dealName = deal.dealName || deal.deal_name || `Deal ${dealId}`
            const customerEmail = deal.customerEmail || deal.customer_email || ''
            const customerLastName = deal.customerName?.split(' ').pop() || ''

            return (
              <Card key={dealId} style={{ marginBottom: '16px' }} padding={false}>
                {/* Deal Header - Clickable */}
                <div
                  onClick={() => selectDeal(deal)}
                  style={{
                    padding: '20px', cursor: 'pointer',
                    borderBottom: `1px solid ${theme.colors.grey200}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
                    {/* Deal Name (full item list) */}
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      lineHeight: 1.4,
                      color: theme.colors.charcoal
                    }}>
                      {dealName}
                    </div>
                    {/* Broken count and customer info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Badge color={brokenCount > 0 ? 'red' : 'green'}>{brokenCount} broken</Badge>
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>
                      {customerEmail} {customerLastName}
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.imBlue, fontWeight: 500 }}>
                      Click to process this deal →
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        // The warehouse row names the pair quoteId/reference;
                        // map to the modal's fields so its first frame shows
                        // the reference code, not the quote UUID.
                        setModalDeal({ id: dealId, quote_id: deal.quoteId, reference_code: deal.reference, dealname: dealName })
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        marginBottom: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: theme.colors.imBlue,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                      title="Open deal details"
                    >
                      Deal #{deal.reference || dealId}
                    </button>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: theme.colors.charcoal }}>
                      {formatCurrency(deal.amount || 0)}
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.grey400 }}>
                      {deal.updatedAt && new Date(deal.updatedAt).toLocaleDateString('en-GB')}
                    </div>
                  </div>
                </div>

                {/* Items Breakdown */}
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '12px' }}>
                    Items ({(deal.items || []).length})
                  </div>
                  {(deal.items || []).map((item, idx) => (
                    <div
                      key={item.id || idx}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        padding: '12px',
                        background: theme.colors.white,
                        borderRadius: theme.radius.md,
                        marginBottom: '8px',
                        border: `1px solid ${theme.colors.grey200}`
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '16px', flexShrink: 0 }}>
                          {item.isBroken ? '❌' : '✅'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                            {item.name || item.productName}
                          </div>
                          {item.isBroken && item.brokenDetails && (
                            <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                              {item.brokenDetails}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <Badge color={item.isBroken ? 'red' : 'green'}>
                          {item.isBroken ? 'Failed - Broken' : `Pass - ${item.overallCondition || 'Unknown'}`}
                        </Badge>
                        {item.isBroken && (
                          <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '4px' }}>
                            Spares: {formatCurrency(item.sparesAmount || 0)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {modalDeal && (
        <DealDetailModal
          deal={modalDeal}
          onClose={() => setModalDeal(null)}
        />
      )}
    </div>
  )
}

export default IssueTrades
